import {Button, IconButton, StylelessButton} from '@dropbox/dig-components/buttons';
import {LayerContext} from '@dropbox/dig-components/layer';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  ChevronDownLine,
  ChevronRightLine,
  CloseLine,
  CodeLine,
  CollectionLine,
  FollowLine,
  FullscreenLine,
  MinusLine,
  OpenLine,
  OrgChartLine,
} from '@dropbox/dig-icons/assets';
import {useInfiniteQuery} from '@tanstack/react-query';
import {logDcdOwnershipServiceGraphInteraction} from 'analytics/dcdOwnershipAnalytics';
import cx from 'classnames';
import {DcdOwnershipService, DcdOwnershipServiceReference, TeamService} from 'client';
import {Link} from 'components/DSYS/Link';
import {Title} from 'components/DSYS/Title';
import {
  getComponentsAndServiceReferenceRoute,
  getComponentsAndServicesServiceRoute,
} from 'constant';
import {t} from 'i18next';
import type {PointerEvent, WheelEvent} from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {getService} from 'utilities';

import {getGrouperGroupUrl, SERVICE_ROUTE_REFERENCE_TYPE} from './ownershipHelpers';
import styles from './OwnershipServiceGraph.module.css';

type GraphTransform = {
  x: number;
  y: number;
  scale: number;
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
};

type ScrollPosition = {
  x: number;
  y: number;
};

const INITIAL_TRANSFORM = {x: 0, y: 0, scale: 1};
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 1.4;
const OVERFLOW_HEIGHT_EPSILON = 2;
const SERVICE_GRAPH_FULLSCREEN_Z_INDEX = 1000;
const SERVICE_REFERENCES_PAGE_SIZE = 15;
const EXPANDED_SERVICE_REFERENCES_PAGE_SIZE = 50;

const clampZoom = (scale: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale));

const ServiceDescription = ({description}: {description: string}) => {
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const [isDescriptionTooltipOpen, setIsDescriptionTooltipOpen] = useState(false);
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = descriptionRef.current;
    if (!element) {
      setIsDescriptionTruncated(false);
      return undefined;
    }

    let animationFrame: number | undefined;
    const measure = () => {
      setIsDescriptionTruncated(
        element.scrollHeight - element.clientHeight > OVERFLOW_HEIGHT_EPSILON
      );
    };
    const scheduleMeasure = () => {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }
      animationFrame = window.requestAnimationFrame(measure);
    };

    measure();
    scheduleMeasure();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleMeasure) : null;
    resizeObserver?.observe(element);

    return () => {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }
      resizeObserver?.disconnect();
    };
  }, [description]);

  return (
    <>
      <Tooltip.Control
        open={isDescriptionTruncated && isDescriptionTooltipOpen}
        triggerRef={descriptionRef}
        placement="top"
        maxWidth={320}
      >
        {description}
      </Tooltip.Control>
      <Box
        ref={descriptionRef}
        className={styles.serviceDescription}
        onMouseEnter={() => setIsDescriptionTooltipOpen(true)}
        onMouseLeave={() => setIsDescriptionTooltipOpen(false)}
      >
        <Text variant="paragraph" size="small" color="subtle" tagName="span">
          {description}
        </Text>
      </Box>
    </>
  );
};

const OwnerGroupNode = ({
  ownerGroupName,
  isGraphFullscreen,
}: {
  ownerGroupName: string;
  isGraphFullscreen: boolean;
}) => {
  const label = t('dcd_ownership_grouper_group_name', {group: ownerGroupName});
  return (
    <Link
      to={getGrouperGroupUrl(ownerGroupName)}
      className={cx(styles.rootNode, styles.nodeLink)}
      hasNoUnderline
      monochromatic
      openInNewTab
      rel="noopener noreferrer"
      aria-label={label}
      onClick={() =>
        logDcdOwnershipServiceGraphInteraction('node_clicked', {
          isGraphFullscreen,
          nodeType: 'grouper',
        })
      }
      onPointerDown={(event) => event.stopPropagation()}
    >
      <UIIcon
        src={CollectionLine}
        className={atoms({
          color: 'Primary Base',
        })}
      />
      <Stack gap="0">
        <Text variant="label" size="medium" isBold tagName="div">
          {t('ownership_service_graph_grouper_id')}
        </Text>
        <Text
          variant="paragraph"
          size="small"
          color="subtle"
          tagName="div"
          className={styles.rootName}
        >
          {label}
        </Text>
      </Stack>
    </Link>
  );
};

const ComponentNode = ({
  componentId,
  componentName,
  componentDescription,
  isGraphFullscreen,
}: {
  componentId: number;
  componentName: string;
  componentDescription?: string | null;
  isGraphFullscreen: boolean;
}) => (
  <Link
    to={getComponentsAndServiceReferenceRoute(componentId)}
    className={cx(styles.componentNode, styles.nodeLink)}
    hasNoUnderline
    monochromatic
    onClick={() =>
      logDcdOwnershipServiceGraphInteraction('node_clicked', {
        isGraphFullscreen,
        nodeType: 'component',
      })
    }
    onPointerDown={(event) => event.stopPropagation()}
  >
    <Stack gap="0" className={styles.componentNodeContent}>
      <Text
        variant="label"
        size="medium"
        isBold
        tagName="div"
        className={styles.componentName}
        title={componentName}
      >
        {componentName}
      </Text>
      {componentDescription && <ServiceDescription description={componentDescription} />}
    </Stack>
  </Link>
);

const ServiceNodeContent = ({
  service,
  isExpanded,
  showReferenceCount = true,
}: {
  service: DcdOwnershipService;
  isExpanded?: boolean;
  showReferenceCount?: boolean;
}) => {
  const referenceCount = service.reference_count ?? 0;
  const hasReferences = referenceCount > 0;

  return (
    <>
      <Box className={styles.serviceHeader}>
        {isExpanded !== undefined && (
          <UIIcon
            src={isExpanded ? ChevronDownLine : ChevronRightLine}
            size="small"
            className={styles.serviceChevron}
          />
        )}
        <Text
          variant="label"
          size="medium"
          isBold
          tagName="div"
          className={styles.serviceName}
          title={service.name}
        >
          {service.name}
        </Text>
      </Box>
      {service.description && <ServiceDescription description={service.description} />}
      {hasReferences && showReferenceCount && (
        <Text variant="paragraph" size="small" tagName="div" className={styles.referenceCount}>
          {t('ownership_service_graph_reference_count', {
            count: referenceCount,
          })}
        </Text>
      )}
    </>
  );
};

const ServiceNode = ({
  componentId,
  service,
  isExpanded,
  onToggle,
  isExpandable,
  isGraphFullscreen,
  showReferenceCount = isExpandable,
  showDetailsLink = true,
}: {
  componentId: number;
  service: DcdOwnershipService;
  isExpanded: boolean;
  onToggle: () => void;
  isExpandable: boolean;
  isGraphFullscreen: boolean;
  showReferenceCount?: boolean;
  showDetailsLink?: boolean;
}) => {
  const hasReferences = Boolean(service.reference_count);
  const canToggle = isExpandable && hasReferences;

  return (
    <Box
      className={cx(styles.serviceNode, {
        [styles.expandedServiceNode]: !isExpandable || isExpanded,
      })}
    >
      {canToggle ? (
        <StylelessButton
          type="button"
          cursor="pointer"
          className={cx(styles.serviceNodeContent, styles.serviceNodeButton)}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${service.name} service references`}
          onClick={onToggle}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <ServiceNodeContent
            service={service}
            isExpanded={isExpanded}
            showReferenceCount={showReferenceCount}
          />
        </StylelessButton>
      ) : (
        <Box className={styles.serviceNodeContent}>
          <ServiceNodeContent service={service} showReferenceCount={showReferenceCount} />
        </Box>
      )}
      {showDetailsLink && (
        <Link
          to={getComponentsAndServicesServiceRoute(componentId, service.id)}
          aria-label={`Open ${service.name} service details`}
          className={styles.serviceDetailsLink}
          hasNoUnderline
          monochromatic
          openInNewTab
          rel="noopener noreferrer"
          onClick={(event) => {
            event.stopPropagation();
            logDcdOwnershipServiceGraphInteraction('open_link_clicked', {
              isGraphFullscreen,
              nodeType: 'service',
            });
          }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <UIIcon src={OpenLine} size="small" />
        </Link>
      )}
    </Box>
  );
};

const ServiceReferenceNode = ({
  componentId,
  reference,
  isGraphFullscreen,
}: {
  componentId: number;
  reference: DcdOwnershipServiceReference;
  isGraphFullscreen: boolean;
}) => {
  const isServiceRoute = reference.reference_type === SERVICE_ROUTE_REFERENCE_TYPE;
  return (
    <Link
      to={getComponentsAndServiceReferenceRoute(componentId, reference.id)}
      className={cx(styles.referenceNode, styles.nodeLink)}
      hasNoUnderline
      monochromatic
      onClick={() =>
        logDcdOwnershipServiceGraphInteraction('node_clicked', {
          isGraphFullscreen,
          nodeType: isServiceRoute ? 'service_route' : 'code_path',
        })
      }
      onPointerDown={(event) => event.stopPropagation()}
    >
      <UIIcon
        src={isServiceRoute ? FollowLine : CodeLine}
        size="small"
        className={cx(styles.referenceIcon, {
          [styles.routeReferenceIcon]: isServiceRoute,
          [styles.codeReferenceIcon]: !isServiceRoute,
        })}
      />
      <Text variant="label" size="small" tagName="div" className={styles.referenceName}>
        <Truncate lines={1} location={0.5} observeParent tooltipControlProps={{placement: 'top'}}>
          {reference.name}
        </Truncate>
      </Text>
    </Link>
  );
};

const ServiceReferencesPanel = ({
  componentId,
  references,
  isLoading,
  isError,
  hasMore,
  isFetchingMore,
  isGraphFullscreen,
  onLoadMore,
}: {
  componentId: number;
  references: DcdOwnershipServiceReference[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  isGraphFullscreen: boolean;
  onLoadMore: () => void;
}) => (
  <Box className={styles.referencesPanel}>
    {isLoading && (
      <Box display="flex" justifyContent="center" className={styles.referencesStatus}>
        <Spinner size="small" />
      </Box>
    )}
    {!isLoading && isError && (
      <Text variant="paragraph" size="small" tagName="div">
        {t('ownership_service_graph_references_error')}
      </Text>
    )}
    {!isLoading && !isError && references.length > 0 && (
      <>
        <Box className={styles.referencesList}>
          {references.map((reference) => (
            <ServiceReferenceNode
              key={reference.id}
              componentId={componentId}
              reference={reference}
              isGraphFullscreen={isGraphFullscreen}
            />
          ))}
        </Box>
        {hasMore && (
          <Box className={styles.viewAllButtonWrapper}>
            <Button
              variant="transparent"
              size="small"
              isLoading={isFetchingMore}
              onClick={onLoadMore}
              onPointerDown={(event: PointerEvent<HTMLButtonElement>) => event.stopPropagation()}
            >
              {t('view_more')}
            </Button>
          </Box>
        )}
      </>
    )}
  </Box>
);

const ServiceGraphServiceSlot = ({
  componentId,
  service,
  isExpanded,
  onToggle,
  referencePageSize,
  isServiceDetail,
  selectedReference,
  isGraphFullscreen,
}: {
  componentId: number;
  service: DcdOwnershipService;
  isExpanded: boolean;
  onToggle: () => void;
  referencePageSize: number;
  isServiceDetail: boolean;
  selectedReference?: DcdOwnershipServiceReference;
  isGraphFullscreen: boolean;
}) => {
  const hasSelectedReference = selectedReference !== undefined;
  const canLoadReferences = !hasSelectedReference;
  const isExpandable = canLoadReferences && !isServiceDetail;
  const shouldLoadReferences =
    canLoadReferences && (isServiceDetail || isExpanded) && Boolean(service.reference_count);
  const referencesQuery = useInfiniteQuery({
    queryKey: ['teams', 'ownership', 'serviceReferences', service.id, referencePageSize],
    queryFn: ({pageParam}) =>
      getService(
        TeamService
      ).getDcdOwnershipServiceReferencesApiV1TeamsOwnershipServicesServiceIdReferencesGet(
        service.id,
        referencePageSize,
        Number(pageParam)
      ),
    enabled: shouldLoadReferences,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
  });
  const serviceReferences = useMemo(() => {
    if (selectedReference) {
      return [selectedReference];
    }
    return referencesQuery.data?.pages.flatMap((page) => page.references) ?? [];
  }, [referencesQuery.data, selectedReference]);
  const showReferences =
    hasSelectedReference || ((isServiceDetail || isExpanded) && Boolean(service.reference_count));

  return (
    <Box className={styles.serviceSlot}>
      <ServiceNode
        componentId={componentId}
        service={service}
        isExpanded={hasSelectedReference || isServiceDetail || isExpanded}
        isExpandable={isExpandable}
        isGraphFullscreen={isGraphFullscreen}
        showReferenceCount={!hasSelectedReference}
        showDetailsLink={!isServiceDetail}
        onToggle={onToggle}
      />
      {showReferences && (
        <ServiceReferencesPanel
          componentId={componentId}
          references={serviceReferences}
          isLoading={canLoadReferences && referencesQuery.isLoading}
          isError={canLoadReferences && referencesQuery.isError}
          hasMore={canLoadReferences && Boolean(referencesQuery.hasNextPage)}
          isFetchingMore={canLoadReferences && referencesQuery.isFetchingNextPage}
          isGraphFullscreen={isGraphFullscreen}
          onLoadMore={() => {
            logDcdOwnershipServiceGraphInteraction('node_view_more', {
              isGraphFullscreen,
              nodeType: 'service',
            });
            void referencesQuery.fetchNextPage();
          }}
        />
      )}
    </Box>
  );
};

const ZoomButton = ({
  ariaLabel,
  onClick,
  zoomIn = false,
}: {
  ariaLabel: string;
  onClick: () => void;
  zoomIn?: boolean;
}) => (
  <IconButton variant="outline" size="small" aria-label={ariaLabel} onClick={onClick}>
    <UIIcon src={zoomIn ? AddLine : MinusLine} />
  </IconButton>
);

export const OwnershipServiceGraph = ({
  componentId,
  componentName,
  componentDescription,
  ownerGroupName,
  services,
  initiallyExpandedServiceId,
  isServiceDetail = false,
  selectedReference,
}: {
  componentId: number;
  componentName: string;
  componentDescription?: string | null;
  ownerGroupName: string;
  services: DcdOwnershipService[];
  initiallyExpandedServiceId?: number;
  isServiceDetail?: boolean;
  selectedReference?: DcdOwnershipServiceReference;
}) => {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const transformLayerRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const expandedScrollPositionRef = useRef<ScrollPosition | null>(null);
  const parentLayerContext = useContext(LayerContext);
  const [expanded, setExpanded] = useState(false);
  const [expandedServiceIds, setExpandedServiceIds] = useState<Set<number>>(
    () => new Set(initiallyExpandedServiceId === undefined ? [] : [initiallyExpandedServiceId])
  );
  const [hasOpenedReferences, setHasOpenedReferences] = useState(false);
  const [transform, setTransform] = useState<GraphTransform>(INITIAL_TRANSFORM);
  const referencePageSize =
    isServiceDetail && expanded
      ? EXPANDED_SERVICE_REFERENCES_PAGE_SIZE
      : SERVICE_REFERENCES_PAGE_SIZE;
  const serviceKey = useMemo(() => services.map((service) => service.id).join(','), [services]);
  const layerContext = useMemo(
    () =>
      expanded
        ? {
            ...(parentLayerContext ?? {}),
            zIndex: SERVICE_GRAPH_FULLSCREEN_Z_INDEX,
          }
        : parentLayerContext,
    [expanded, parentLayerContext]
  );

  const handleServiceToggle = useCallback(
    (service: DcdOwnershipService) => {
      if (!service.reference_count) {
        return;
      }

      const isServiceExpanded = expandedServiceIds.has(service.id);
      logDcdOwnershipServiceGraphInteraction(
        isServiceExpanded ? 'node_collapsed' : 'node_expanded',
        {
          isGraphFullscreen: expanded,
          nodeType: 'service',
        }
      );
      setHasOpenedReferences(true);
      setExpandedServiceIds((current) => {
        const next = new Set(current);

        if (isServiceExpanded) {
          next.delete(service.id);
        } else {
          next.add(service.id);
        }

        return next;
      });
    },
    [expanded, expandedServiceIds]
  );

  const expandGraph = useCallback(() => {
    expandedScrollPositionRef.current = {
      x: window.scrollX,
      y: window.scrollY,
    };
    logDcdOwnershipServiceGraphInteraction('graph_expanded', {isGraphFullscreen: expanded});
    setExpanded(true);
  }, [expanded]);

  const collapseGraph = useCallback(() => {
    logDcdOwnershipServiceGraphInteraction('graph_collapsed', {isGraphFullscreen: expanded});
    setExpanded(false);
  }, [expanded]);

  const resetAndCenterGraph = useCallback(() => {
    const frame = frameRef.current;
    const transformLayer = transformLayerRef.current;
    const centeredX =
      frame && transformLayer
        ? Math.min(0, (frame.clientWidth - transformLayer.scrollWidth) / 2)
        : 0;

    setTransform({
      ...INITIAL_TRANSFORM,
      x: centeredX,
    });
    dragStateRef.current = null;
  }, []);

  const zoomAtPoint = useCallback((zoomFactor: number, pointX: number, pointY: number) => {
    setTransform((current) => {
      const nextScale = clampZoom(current.scale * zoomFactor);
      const scaleRatio = nextScale / current.scale;

      return {
        scale: nextScale,
        x: pointX - (pointX - current.x) * scaleRatio,
        y: pointY - (pointY - current.y) * scaleRatio,
      };
    });
  }, []);

  const adjustZoom = useCallback(
    (zoomFactor: number) => {
      const frame = frameRef.current;
      const pointX = frame ? frame.clientWidth / 2 : 0;
      const pointY = frame ? frame.clientHeight / 2 : 0;

      zoomAtPoint(zoomFactor, pointX, pointY);
    },
    [zoomAtPoint]
  );

  const handleWheel = useCallback(
    (event: WheelEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (event.ctrlKey || event.metaKey) {
        const frame = frameRef.current;
        const rect = frame?.getBoundingClientRect();
        const pointX = rect ? event.clientX - rect.left : 0;
        const pointY = rect ? event.clientY - rect.top : 0;
        const zoomFactor = event.deltaY > 0 ? 0.96 : 1.04;

        zoomAtPoint(zoomFactor, pointX, pointY);
        return;
      }

      setTransform((current) => ({
        ...current,
        x: current.x - event.deltaX,
        y: current.y - event.deltaY,
      }));
    },
    [zoomAtPoint]
  );

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      event.currentTarget.setPointerCapture(event.pointerId);
      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: transform.x,
        originY: transform.y,
      };
    },
    [transform.x, transform.y]
  );

  const handlePointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    setTransform((current) => ({
      ...current,
      x: dragState.originX + event.clientX - dragState.startX,
      y: dragState.originY + event.clientY - dragState.startY,
    }));
  }, []);

  const handlePointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    event.currentTarget.releasePointerCapture(event.pointerId);
    dragStateRef.current = null;
  }, []);

  useLayoutEffect(() => {
    const animationFrame = window.requestAnimationFrame(resetAndCenterGraph);

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [
    componentDescription,
    componentName,
    expanded,
    ownerGroupName,
    resetAndCenterGraph,
    serviceKey,
  ]);

  useLayoutEffect(() => {
    if (!expanded) {
      return undefined;
    }

    const scrollPosition = expandedScrollPositionRef.current ?? {
      x: window.scrollX,
      y: window.scrollY,
    };
    const originalBodyOverflow = document.body.style.overflow;
    const originalDocumentElementOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalDocumentElementOverflow;
      window.scrollTo(scrollPosition.x, scrollPosition.y);
      expandedScrollPositionRef.current = null;
    };
  }, [expanded]);

  useEffect(() => {
    setHasOpenedReferences(false);
    setExpandedServiceIds(
      new Set(initiallyExpandedServiceId === undefined ? [] : [initiallyExpandedServiceId])
    );
  }, [initiallyExpandedServiceId, ownerGroupName, serviceKey]);

  useEffect(() => {
    if (!expanded) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        collapseGraph();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [collapseGraph, expanded]);

  if (!services.length) {
    return null;
  }

  return (
    <LayerContext.Provider value={layerContext}>
      <Stack
        gap="16"
        className={cx(
          styles.graphCard,
          {
            [styles.expandedGraphCard]: expanded,
          },
          atoms({
            paddingX: '20',
            paddingY: '16',
            borderRadius: 'Medium',
          })
        )}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Title size="small" withAccessoryStart={<UIIcon src={OrgChartLine} />}>
            {t('ownership_service_graph')}
          </Title>
          <Box display="flex" alignItems="center" style={{gap: 8}}>
            {expanded && (
              <>
                <ZoomButton ariaLabel="Zoom out service graph" onClick={() => adjustZoom(0.8)} />
                <ZoomButton
                  zoomIn
                  ariaLabel="Zoom in service graph"
                  onClick={() => adjustZoom(1.2)}
                />
              </>
            )}
            <IconButton
              variant={expanded ? 'filled' : 'outline'}
              aria-label={expanded ? 'Collapse service graph' : 'Expand service graph'}
              onClick={expanded ? collapseGraph : expandGraph}
            >
              <UIIcon
                src={expanded ? CloseLine : FullscreenLine}
                className={atoms({color: expanded ? 'Text Base' : 'Text Subtle'})}
              />
            </IconButton>
          </Box>
        </Box>
        <Box
          ref={frameRef}
          className={cx(styles.graphFrame, {
            [styles.graphFrameWithReferences]:
              selectedReference || expandedServiceIds.size > 0 || hasOpenedReferences,
          })}
          onWheel={expanded ? handleWheel : undefined}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <Box
            ref={transformLayerRef}
            className={styles.transformLayer}
            style={{
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            }}
          >
            <Box
              className={cx(styles.graph, {
                [styles.serviceDetailGraph]: isServiceDetail,
                [styles.expandedServiceDetailGraph]: isServiceDetail && expanded,
              })}
            >
              <OwnerGroupNode ownerGroupName={ownerGroupName} isGraphFullscreen={expanded} />
              <Box className={styles.rootConnector} />
              <ComponentNode
                componentId={componentId}
                componentName={componentName}
                componentDescription={componentDescription}
                isGraphFullscreen={expanded}
              />
              <Box className={styles.rootConnector} />
              <Box
                className={cx(styles.serviceBranches, {
                  [styles.hasMultipleServices]: services.length > 1,
                  [styles.hasSingleService]: services.length === 1,
                })}
              >
                <Box className={styles.servicesRow}>
                  {services.map((service) => (
                    <ServiceGraphServiceSlot
                      key={service.id}
                      componentId={componentId}
                      service={service}
                      isExpanded={expandedServiceIds.has(service.id)}
                      referencePageSize={referencePageSize}
                      isServiceDetail={isServiceDetail}
                      selectedReference={selectedReference}
                      isGraphFullscreen={expanded}
                      onToggle={() => handleServiceToggle(service)}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Stack>
    </LayerContext.Provider>
  );
};
